import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

const WebSocketContext = createContext(null);

export function WebSocketProvider({ children }) {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const listenersRef = useRef(new Map());
  const [connected, setConnected] = useState(false);
  const reconnectTimer = useRef(null);
  const tokenRef = useRef(null);

  const fetchToken = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/ws-token");
      tokenRef.current = data.token;
      return data.token;
    } catch {
      return null;
    }
  }, []);

  const connect = useCallback(async () => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    let token = tokenRef.current;
    if (!token) token = await fetchToken();
    if (!token) return;

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    if (!backendUrl) return;
    const wsBase = backendUrl.replace(/^http/, "ws");
    const url = `${wsBase}/api/ws?token=${token}`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => setConnected(true);

      ws.onmessage = (evt) => {
        try {
          const { event, data } = JSON.parse(evt.data);
          const handlers = listenersRef.current.get(event);
          if (handlers) handlers.forEach((fn) => fn(data));
        } catch (err) { console.error("WS message parse error:", err); }
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
        reconnectTimer.current = setTimeout(connect, 3000);
      };

      ws.onerror = () => ws.close();
    } catch (err) { console.error("WS connect error:", err); }
  }, [fetchToken]);

  useEffect(() => {
    if (user) {
      connect();
    } else {
      if (wsRef.current) wsRef.current.close();
      wsRef.current = null;
      tokenRef.current = null;
      setConnected(false);
    }
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) wsRef.current.close();
    };
  }, [user, connect]);

  const on = useCallback((event, handler) => {
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, new Set());
    }
    listenersRef.current.get(event).add(handler);
    return () => {
      const set = listenersRef.current.get(event);
      if (set) set.delete(handler);
    };
  }, []);

  return (
    <WebSocketContext.Provider value={{ connected, on }}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWs() {
  return useContext(WebSocketContext);
}
